import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { AppShell } from "./components/AppShell";
import { CaptureView } from "./components/CaptureView";
import { InstallPrompt } from "./components/InstallPrompt";
import { MicrophoneOnboarding } from "./components/MicrophoneOnboarding";
import { NotesView } from "./components/NotesView";
import { ReviewView } from "./components/ReviewView";
import { SettingsView } from "./components/SettingsView";
import { SignInView } from "./components/SignInView";
import {
  ApiError,
  bootstrapGoogleConnection,
  createCalendarEvent,
  createGoogleDoc,
  disconnectGoogle,
  fetchGoogleStatus,
  fetchHealth,
  processCapture
} from "./lib/api";
import {
  CONVERSATION_WINDOW,
  appendConversationMessage,
  clearConversationMessages,
  clearLocalPreferences,
  createNote,
  deleteNote,
  getNote,
  listActivity,
  listNotes,
  loadConversationMessages,
  loadPreferences,
  loadPreferencesForUser,
  pruneConversationMessages,
  recordActivity,
  savePreferences,
  updateConversationMessage,
  updateNote
} from "./lib/storage";
import {
  currentSession,
  isSupabaseBrowserConfigured,
  signInWithGoogle,
  signOut,
  scrubGoogleProviderTokens,
  supabase
} from "./lib/supabase";
import { usePwaInstall } from "./lib/usePwaInstall";
import { useSpeechSynthesis } from "./lib/useSpeechSynthesis";
import type {
  ActivityEntry,
  AppTab,
  ConversationMessage,
  Destination,
  GoogleConnectionStatus,
  Note,
  Preferences,
  ProcessedCapture,
  ProviderHealth
} from "./types";

const initialHealth: ProviderHealth = {
  authGoogle: false,
  providers: { supabase: false, gemini: false, google: false }
};

const initialGoogle: GoogleConnectionStatus = { connected: false, docs: false, calendar: false };

const AUTO_COUNTDOWN_SECONDS = 5;
const SUCCESS_TOAST_MS = 6_000;

const DESTINATION_LABELS: Record<Exclude<Destination, "unknown">, string> = {
  note: "une note ParleNote",
  google_docs: "un document Google",
  calendar: "un événement Google Agenda"
};

/** Maps the quick answers offered for the `destination` question. */
const DESTINATION_ANSWERS: Array<{ match: RegExp; destination: Exclude<Destination, "unknown"> }> = [
  { match: /(agenda|calendrier|calendar|evenement|rendez)/u, destination: "calendar" },
  { match: /(docs?|document)/u, destination: "google_docs" },
  { match: /(note|memo|parlenote)/u, destination: "note" }
];

function foldAccents(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[‘’]/gu, "'")
    .toLocaleLowerCase("fr-FR");
}

function destinationFromAnswer(answer: string): Exclude<Destination, "unknown"> | undefined {
  const folded = foldAccents(answer);
  return DESTINATION_ANSWERS.find((entry) => entry.match.test(folded))?.destination;
}

function localDate(offsetDays: number): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
}

function normalizedAnswer(id: string, answer: string): string {
  if (id === "date") {
    const folded = foldAccents(answer);
    if (folded === "aujourd'hui" || folded === "aujourd’hui") return localDate(0);
    if (folded === "demain") return localDate(1);
  }
  if (id === "time") {
    const match = answer.match(/^(\d{1,2})\s*h(?:\s*(\d{2}))?$/iu);
    if (match) return `${match[1].padStart(2, "0")}:${match[2] || "00"}`;
  }
  return answer;
}

function eventRange(capture: ProcessedCapture): { start: string; end: string } {
  const date = capture.event?.date;
  const time = capture.event?.time;
  if (!date || !time || !/^\d{4}-\d{2}-\d{2}$/u.test(date) || !/^\d{2}:\d{2}$/u.test(time)) {
    throw new Error("Choisissez une date et une heure précises avant de créer l’événement.");
  }
  const start = new Date(`${date}T${time}:00`);
  if (Number.isNaN(start.getTime())) throw new Error("La date ou l’heure n’est pas valide.");
  const end = new Date(start.getTime() + (capture.event?.durationMinutes || 30) * 60_000);
  return { start: start.toISOString(), end: end.toISOString() };
}

function assistantResponse(capture: ProcessedCapture): string {
  if (capture.destination === "unknown" && capture.assistantReply?.trim()) {
    return capture.assistantReply.trim();
  }
  const question = capture.questions[0]?.prompt;
  if (question) return `J’ai compris l’essentiel. ${question}`;
  if (capture.destination === "unknown") {
    return "Je peux transformer vos idées en notes ParleNote, préparer un document Google ou organiser un événement dans Google Agenda. Dites-moi simplement ce que vous souhaitez faire.";
  }
  if (capture.destination === "note" && capture.operation === "append") {
    return `J’ai préparé un ajout à la note « ${capture.targetTitle || capture.title} ». Relisez puis confirmez.`;
  }
  if (capture.destination === "note" && capture.operation === "update") {
    return `J’ai préparé la modification de la note « ${capture.targetTitle || capture.title} ». Relisez puis confirmez.`;
  }
  if (capture.destination === "note" && capture.operation === "delete") {
    return `Vous êtes sur le point de supprimer la note « ${capture.targetTitle || capture.title} ». Relisez-la puis confirmez.`;
  }
  return `J’ai préparé ${DESTINATION_LABELS[capture.destination]} intitulé « ${capture.title} ». Relisez puis confirmez.`;
}

function normalizedNoteTitle(value: string): string {
  return value.trim().toLocaleLowerCase("fr-FR").normalize("NFKC");
}

function localMessage(role: "user" | "assistant", content: string): ConversationMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    createdAt: new Date().toISOString()
  };
}

interface Failure {
  message: string;
  /** Why it happened and what it means for the message that was being sent. */
  hint?: string;
}

function describeFailure(caught: unknown): Failure {
  if (caught instanceof ApiError) {
    let message = caught.message;
    if (caught.retryAfterSeconds) {
      const minutes = Math.ceil(caught.retryAfterSeconds / 60);
      message += minutes > 1 ? ` Réessayez dans ${minutes} min.` : " Réessayez dans un instant.";
    }
    return { message, hint: caught.hint };
  }
  return {
    message: caught instanceof Error ? caught.message : "L’action a échoué."
  };
}

function failureMessage(caught: unknown): string {
  return describeFailure(caught).message;
}

export default function App() {
  const [tab, setTab] = useState<AppTab>("capture");
  const [capture, setCapture] = useState<ProcessedCapture>();
  const [targetNote, setTargetNote] = useState<Note>();
  const [notes, setNotes] = useState<Note[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [health, setHealth] = useState<ProviderHealth>(initialHealth);
  const [google, setGoogle] = useState<GoogleConnectionStatus>(initialGoogle);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [signInBusy, setSignInBusy] = useState(false);
  const [signInError, setSignInError] = useState("");
  const [preferences, setPreferences] = useState<Preferences>(loadPreferences);
  const [busy, setBusy] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [actionLink, setActionLink] = useState<{ label: string; url: string }>();
  const [problem, setProblem] = useState<Failure>();
  const [autoCountdown, setAutoCountdown] = useState<number>();

  const userId = session?.user.id;
  const speech = useSpeechSynthesis(preferences.autoSpeak);
  const pwa = usePwaInstall(Boolean(session));

  // `execute` runs from a countdown effect, so it must stay referentially
  // stable. Reading the note list through a ref keeps it from capturing a
  // snapshot that was already stale when the timer fired.
  /** The provider token already exchanged, so sign-in cannot race itself. */
  const bootstrappedTokenRef = useRef<string | undefined>(undefined);
  const notesRef = useRef<Note[]>(notes);
  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  const refreshNotes = useCallback(async () => {
    if (!userId) return;
    setNotesLoading(true);
    try {
      setNotes(await listNotes(userId));
    } catch {
      setProblem({ message: "Impossible de charger les notes." });
    } finally {
      setNotesLoading(false);
    }
  }, [userId]);

  const refreshActivity = useCallback(async () => {
    if (!userId) {
      setActivity([]);
      return;
    }
    // Purely informational, so a failure here stays silent.
    setActivity(await listActivity(userId).catch(() => []));
  }, [userId]);

  const refreshGoogle = useCallback(async () => {
    if (!userId) {
      setGoogle(initialGoogle);
      return;
    }
    try {
      setGoogle(await fetchGoogleStatus());
    } catch {
      setGoogle(initialGoogle);
    }
  }, [userId]);

  const bootstrapGoogle = useCallback(async (nextSession: Session | null) => {
    if (!nextSession?.provider_token) return;
    // Sign-in fires this from both the initial session read and the SIGNED_IN
    // event. Two concurrent exchanges of the same token raced each other on the
    // same row and one of them lost.
    if (bootstrappedTokenRef.current === nextSession.provider_token) return;
    bootstrappedTokenRef.current = nextSession.provider_token;
    try {
      const status = await bootstrapGoogleConnection(
        nextSession.provider_token,
        nextSession.provider_refresh_token || undefined
      );
      setGoogle(status);
    } catch (caught) {
      // The server names the actual cause — a token issued for another OAuth
      // client, a missing refresh token, a mismatched account. Swallowing it
      // and showing "renouvelez les accès" made the real problem invisible and
      // sent people round a loop that could never fix it.
      const described = describeFailure(caught);
      setProblem({
        message: `Accès Docs et Agenda non finalisés : ${described.message}`,
        hint: described.hint || "La connexion Google elle-même a réussi."
      });
    } finally {
      // The provider tokens leave the browser session whether or not the
      // exchange succeeded: they must never linger in local storage.
      await scrubGoogleProviderTokens(nextSession).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    fetchHealth().then(setHealth).catch(() => setHealth(initialHealth));
    currentSession()
      .then((current) => {
        setSession(current);
        void bootstrapGoogle(current);
      })
      .finally(() => setSessionReady(true));
    const subscription = supabase?.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      if (event === "SIGNED_IN") void bootstrapGoogle(nextSession);
    });
    return () => subscription?.data.subscription.unsubscribe();
  }, [bootstrapGoogle]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => {
      setMessage("");
      setActionLink(undefined);
    }, SUCCESS_TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (tab === "notes") void refreshNotes();
  }, [refreshNotes, tab]);

  useEffect(() => {
    if (!userId) {
      // Leave nothing from the previous account behind on a shared device.
      setMessages([]);
      setNotes([]);
      setCapture(undefined);
      setActivity([]);
      setTab("capture");
      return;
    }
    void refreshNotes();
    void refreshGoogle();
    void refreshActivity();
    loadPreferencesForUser(userId)
      .then(async (nextPreferences) => {
        setPreferences(nextPreferences);
        // Retention is applied before reading so the list matches what is kept.
        await pruneConversationMessages(nextPreferences.historyDays).catch(() => undefined);
        return loadConversationMessages(userId, nextPreferences.historyDays);
      })
      .then(setMessages)
      .catch(() => setProblem({ message: "Certaines préférences n’ont pas pu être chargées." }));
  }, [refreshActivity, refreshGoogle, refreshNotes, userId]);

  // The note an append or update targets, loaded so the review can show what is
  // already written there instead of an apparently empty note.
  useEffect(() => {
    const operation = capture?.operation;
    if (!capture || capture.destination !== "note" || operation === "create" || !operation) {
      setTargetNote(undefined);
      return;
    }
    let active = true;
    const wanted = capture.targetTitle || capture.title;
    const resolve = async () =>
      (capture.targetNoteId ? await getNote(capture.targetNoteId, userId) : undefined) ||
      notesRef.current.find(
        (note) => normalizedNoteTitle(note.title) === normalizedNoteTitle(wanted)
      );
    resolve()
      .then((note) => {
        if (active) setTargetNote(note);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [capture, userId]);

  const permission = useMemo(() => {
    if (!capture || capture.destination === "unknown") return "always_ask";
    return preferences[capture.destination];
  }, [capture, preferences]);

  const recordMessage = useCallback(
    async (role: "user" | "assistant", content: string) => {
      const fallback = localMessage(role, content);
      setMessages((current) => [...current, fallback].slice(-CONVERSATION_WINDOW));
      if (!userId) return fallback;
      try {
        const persisted = await appendConversationMessage(userId, role, content);
        setMessages((current) =>
          current.map((item) => (item.id === fallback.id ? persisted : item))
        );
        return persisted;
      } catch {
        return fallback;
      }
    },
    [userId]
  );

  const executeNote = useCallback(
    async (target: ProcessedCapture) => {
      const operation = target.operation || "create";
      if (operation === "create") {
        await createNote(
          { title: target.title, body: target.body, sourceTranscript: target.transcript },
          userId
        );
        setMessage("Note enregistrée dans ParleNote.");
        await recordMessage("assistant", `La note « ${target.title} » est enregistrée.`);
        return;
      }

      const targetTitle = target.targetTitle || target.title;
      // The agent searched the notes itself and returned the identifier it
      // actually retrieved, so that is the authoritative target. Matching on a
      // normalised title is only the fallback, and used to fail outright as soon
      // as the wording differed at all.
      const existing =
        (target.targetNoteId ? await getNote(target.targetNoteId, userId) : undefined) ||
        notesRef.current.find(
          (note) => normalizedNoteTitle(note.title) === normalizedNoteTitle(targetTitle)
        );
      if (!existing) {
        throw new Error(
          `Je ne trouve pas la note « ${targetTitle} ». Vérifiez son titre avant de continuer.`
        );
      }

      if (operation === "delete") {
        await deleteNote(existing.id, userId);
        setMessage(`Note « ${existing.title} » supprimée.`);
        await recordMessage("assistant", `La note « ${existing.title} » a été supprimée.`);
        return;
      }
      const nextBody =
        operation === "append"
          ? [existing.body.trim(), target.body.trim()].filter(Boolean).join("\n\n")
          : target.body;
      await updateNote(
        existing.id,
        { title: operation === "update" ? target.title : existing.title, body: nextBody },
        userId
      );
      setMessage(operation === "append" ? "Contenu ajouté à la note." : "Note modifiée.");
      await recordMessage(
        "assistant",
        operation === "append"
          ? `Le contenu a été ajouté à la note « ${existing.title} ».`
          : `La note « ${existing.title} » a été modifiée.`
      );
    },
    [recordMessage, userId]
  );

  const execute = useCallback(async () => {
    if (!capture || capture.destination === "unknown") return;
    if (preferences[capture.destination] === "never") {
      setProblem({ message: "Cette destination est désactivée dans vos permissions." });
      return;
    }
    if (capture.destination === "google_docs" && !preferences.googleDocsEnabled) {
      setProblem({ message: "Google Docs est désactivé dans les réglages." });
      return;
    }
    if (capture.destination === "calendar" && !preferences.calendarEnabled) {
      setProblem({ message: "Google Agenda est désactivé dans les réglages." });
      return;
    }

    setBusy(true);
    setProblem(undefined);
    setActionLink(undefined);
    setAutoCountdown(undefined);
    try {
      if (capture.destination === "note") {
        await executeNote(capture);
        setCapture(undefined);
        setTab("notes");
        await refreshNotes();
      } else if (!google.connected || !userId) {
        throw new Error(
          "Les autorisations Google ne sont pas finalisées. Reconnectez-vous dans les réglages."
        );
      } else if (capture.destination === "google_docs") {
        const document = await createGoogleDoc(capture.title, capture.body);
        setMessage(document.warning || "Document créé dans Google Docs.");
        setActionLink({ label: "Ouvrir le document", url: document.url });
        // Logged so the link survives the toast; a logging failure must never
        // undo a creation that already succeeded.
        await recordActivity(userId, {
          destination: "google_docs",
          title: capture.title,
          externalId: document.id,
          externalUrl: document.url
        }).catch(() => undefined);
        await recordMessage("assistant", `Le document « ${capture.title} » a été créé.`);
        setCapture(undefined);
        void refreshActivity();
      } else {
        const { start, end } = eventRange(capture);
        const event = await createCalendarEvent({
          title: capture.title,
          description: capture.body,
          start,
          end,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        setMessage("Événement créé dans Google Agenda.");
        if (event.url) setActionLink({ label: "Ouvrir dans Agenda", url: event.url });
        await recordActivity(userId, {
          destination: "calendar",
          title: capture.title,
          externalId: event.id,
          externalUrl: event.url
        }).catch(() => undefined);
        await recordMessage("assistant", `L’événement « ${capture.title} » a été créé.`);
        setCapture(undefined);
        void refreshActivity();
      }
    } catch (caught) {
      setProblem(describeFailure(caught));
    } finally {
      setBusy(false);
    }
  }, [
    capture,
    executeNote,
    google.connected,
    preferences,
    recordMessage,
    refreshActivity,
    refreshNotes,
    userId
  ]);

  const externalReady =
    capture?.destination === "note" || (google.connected && Boolean(userId));

  useEffect(() => {
    const eligible =
      capture &&
      capture.destination !== "unknown" &&
      capture.questions.length === 0 &&
      (capture.operation || "create") === "create" &&
      // An event the agent found clashing with an existing one is ambiguous by
      // definition, and ambiguity always goes back to the person.
      !capture.conflicts?.length &&
      permission === "guarded_auto" &&
      externalReady;
    setAutoCountdown(eligible ? AUTO_COUNTDOWN_SECONDS : undefined);
  }, [capture, externalReady, permission]);

  useEffect(() => {
    if (autoCountdown === undefined) return;
    if (autoCountdown <= 0) {
      void execute();
      return;
    }
    const timer = window.setTimeout(() => setAutoCountdown(autoCountdown - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [autoCountdown, execute]);

  const handleProcess = async (
    transcript: string,
    audio?: Blob,
    turnstileToken?: string,
    currentDraft?: ProcessedCapture
  ): Promise<boolean> => {
    if (!session) {
      setProblem({ message: "Connectez-vous avec Google avant d’utiliser la capture." });
      return false;
    }
    const userText = transcript.trim() || "Message vocal";
    const userMessage = audio ? undefined : await recordMessage("user", userText);
    setBusy(true);
    setProblem(undefined);
    try {
      const result = await processCapture(
        transcript,
        audio,
        turnstileToken,
        userMessage ? [...messages, userMessage] : messages,
        currentDraft
      );
      const conversationalReply =
        result.destination === "unknown" &&
        result.questions.length === 0 &&
        !result.title.trim() &&
        !result.body.trim();
      setCapture(conversationalReply ? undefined : result);
      const cleaned = result.transcript.trim();
      if (audio) {
        await recordMessage("user", cleaned || userText);
      } else if (userMessage && cleaned && cleaned !== userText) {
        // Dictation arrives unpunctuated and sometimes stuttering. The server
        // returns it rewritten, so the conversation shows the sentence as it
        // was meant, not the raw recognition output.
        setMessages((current) =>
          current.map((item) => (item.id === userMessage.id ? { ...item, content: cleaned } : item))
        );
        if (userId) {
          await updateConversationMessage(userMessage.id, cleaned, userId).catch(() => undefined);
        }
      }
      const response = assistantResponse(result);
      await recordMessage("assistant", response);
      speech.speak(response);
      if (result.remaining && result.remaining.short <= 2) {
        setMessage(`Il vous reste ${result.remaining.short} traitement(s) sur ces 10 minutes.`);
      }
      return true;
    } catch (caught) {
      const described = describeFailure(caught);
      const response = described.hint
        ? `${described.message} ${described.hint}`
        : `${described.message} Votre message est conservé : vous pouvez réessayer.`;
      if (audio) await recordMessage("user", userText);
      setProblem(described);
      await recordMessage("assistant", response);
      speech.speak(response);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const updateCapture = (field: "title" | "body", value: string) => {
    setCapture((current) => {
      if (!current) return current;
      // The title question is answered by typing in the field itself, so it has
      // to clear with it — otherwise it stayed open for ever and kept the
      // confirm button disabled.
      const questions =
        field === "title" && value.trim()
          ? current.questions.filter((question) => question.id !== "title")
          : current.questions;
      return { ...current, [field]: value, questions };
    });
  };

  const setDestination = (destination: Exclude<Destination, "unknown">) => {
    setCapture((current) => {
      if (!current) return current;
      const questions = current.questions.filter(
        (question) =>
          question.id !== "destination" &&
          (destination === "calendar" || (question.id !== "date" && question.id !== "time"))
      );
      if (destination === "calendar") {
        if (!current.event?.date && !questions.some((question) => question.id === "date")) {
          questions.push({ id: "date", prompt: "À quelle date ?", options: ["Aujourd’hui", "Demain"] });
        }
        if (!current.event?.time && !questions.some((question) => question.id === "time")) {
          questions.push({ id: "time", prompt: "À quelle heure ?", options: ["9 h", "10 h", "14 h"] });
        }
      }
      return { ...current, destination, questions };
    });
  };

  const updateEvent = (field: "date" | "time" | "durationMinutes", value: string) => {
    setCapture((current) => {
      if (!current) return current;
      return {
        ...current,
        event: {
          date: current.event?.date,
          time: current.event?.time,
          durationMinutes: current.event?.durationMinutes || 30,
          [field]: field === "durationMinutes" ? Number(value) : value
        },
        questions: current.questions.filter((question) => question.id !== field)
      };
    });
  };

  const answerQuestion = (id: string, answer: string) => {
    void recordMessage("user", answer);
    // Answering "where should this go?" used to only dismiss the question,
    // leaving the draft with no destination and the confirm button disabled.
    if (id === "destination") {
      const destination = destinationFromAnswer(answer);
      if (destination) {
        setDestination(destination);
        return;
      }
    }
    const resolved = normalizedAnswer(id, answer);
    setCapture((current) => {
      if (!current) return current;
      return {
        ...current,
        event:
          id === "date" || id === "time"
            ? { durationMinutes: 30, ...current.event, [id]: resolved }
            : current.event,
        questions: current.questions.filter((question) => question.id !== id)
      };
    });
  };

  const updatePreferences = (next: Preferences) => {
    setPreferences(next);
    savePreferences(next, userId)
      .then(() => setMessage("Réglages enregistrés."))
      .catch(() => setProblem({ message: "Impossible d’enregistrer les réglages." }));
  };

  const requestMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMessage("Microphone autorisé.");
    } catch {
      setProblem({
        message: "Le microphone reste bloqué.",
        hint: "L’autorisation se change dans les réglages du site de votre navigateur, puis rechargez la page."
      });
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      const status = await disconnectGoogle();
      setGoogle(status);
      setMessage(
        status.revoked
          ? "Accès Google retiré et autorisation révoquée."
          : "Accès Google retiré. Vérifiez aussi la page des autorisations de votre compte Google."
      );
    } catch (caught) {
      setProblem(describeFailure(caught));
    }
  };

  const handleSignOut = async () => {
    speech.cancel();
    clearLocalPreferences();
    setPreferences(loadPreferences());
    setCapture(undefined);
    setNotes([]);
    setActivity([]);
    setMessages([]);
    setGoogle(initialGoogle);
    await signOut();
  };

  if (!sessionReady) {
    return (
      <AppShell activeTab="capture" onTabChange={() => undefined} navigation={false}>
        <div className="app-loading" role="status">Vérification de votre session…</div>
      </AppShell>
    );
  }

  if (!session) {
    return (
      <AppShell activeTab="capture" onTabChange={() => undefined} navigation={false}>
        <SignInView
          configured={isSupabaseBrowserConfigured() && health.authGoogle}
          busy={signInBusy}
          error={signInError}
          onSignIn={async () => {
            setSignInBusy(true);
            setSignInError("");
            try {
              await signInWithGoogle();
            } catch (caught) {
              setSignInError(failureMessage(caught));
              setSignInBusy(false);
            }
          }}
        />
      </AppShell>
    );
  }

  const authenticatedContent = capture ? (
    <ReviewView
      capture={capture}
      busy={busy}
      autoCountdown={autoCountdown}
      turnstileSiteKey={health.turnstileSiteKey}
      onChange={updateCapture}
      onDestination={setDestination}
      onAnswer={answerQuestion}
      onEventChange={updateEvent}
      onFollowUp={(text, audio, token) => handleProcess(text, audio, token, capture)}
      onCancelAuto={() => setAutoCountdown(undefined)}
      onCancel={() => setCapture(undefined)}
      onConfirm={execute}
      targetNote={targetNote}
    />
  ) : tab === "notes" ? (
    <NotesView
      notes={notes}
      activity={activity}
      loading={notesLoading}
      onCapture={() => setTab("capture")}
      onCreate={async (title, body) => {
        await createNote({ title, body }, userId);
        setMessage("Note créée dans ParleNote.");
        await refreshNotes();
      }}
      onUpdate={async (note, title, body) => {
        await updateNote(note.id, { title, body }, userId);
        setMessage("Note mise à jour.");
        await refreshNotes();
      }}
      onDelete={async (note) => {
        await deleteNote(note.id, userId);
        setMessage("Note supprimée.");
        await refreshNotes();
      }}
    />
  ) : tab === "settings" ? (
    <SettingsView
      preferences={preferences}
      health={health}
      google={google}
      userEmail={session.user.email}
      voiceName={speech.voiceName}
      onPreferences={updatePreferences}
      onRequestMicrophone={requestMicrophone}
      onReconnectGoogle={signInWithGoogle}
      onSignOut={handleSignOut}
      onDisconnectGoogle={handleDisconnectGoogle}
      onClearHistory={async () => {
        if (!userId || !window.confirm("Effacer tout l’historique de conversation ?")) return;
        await clearConversationMessages(userId);
        setMessages([]);
        setMessage("Conversation effacée.");
      }}
    />
  ) : (
    <CaptureView
      busy={busy}
      messages={messages}
      speaking={speech.speaking}
      onToggleSpeech={() => {
        if (speech.speaking) speech.cancel();
        else {
          const latest = [...messages].reverse().find((item) => item.role === "assistant");
          if (latest) speech.speak(latest.content);
        }
      }}
      turnstileSiteKey={health.turnstileSiteKey}
      autoStopOnSilence={preferences.autoStopOnSilence}
      onProcess={handleProcess}
    />
  );

  return (
    <AppShell activeTab={tab} onTabChange={setTab}>
      {message && (
        <div className="toast success" role="status">
          <span>{message}</span>
          {actionLink && (
            <a href={actionLink.url} target="_blank" rel="noreferrer">
              {actionLink.label}
            </a>
          )}
          <button
            type="button"
            onClick={() => {
              setMessage("");
              setActionLink(undefined);
            }}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>
      )}
      {problem && (
        <div className="toast error" role="alert">
          <span>
            {problem.message}
            {/* Says plainly whether the message left ParleNote, was charged,
                and whether retrying is worth anything. */}
            {problem.hint && <small>{problem.hint}</small>}
          </span>
          <button type="button" onClick={() => setProblem(undefined)} aria-label="Fermer">×</button>
        </div>
      )}
      {pwa.visible && (
        <InstallPrompt
          isiOS={pwa.isiOS}
          canPrompt={pwa.canPrompt}
          onInstall={pwa.install}
          onDismiss={pwa.dismiss}
        />
      )}
      <MicrophoneOnboarding authenticated onReady={() => setMessage("Microphone prêt.")} />
      {authenticatedContent}
    </AppShell>
  );
}
